// Extracted from web/components/WorkbenchDialogs.tsx; behavior and declaration bodies preserved.
import { useEffect, useState } from "react";

import { Search } from "lucide-react";
import type { Diagnostic, ModelDocument, Operation, Repository } from "../../../shared/contracts";

import { createSubjectArea, extractModel } from "../../../core";

import { Button, Diagnostics, Field, LossSummary, Modal, Notice, errorText } from "../../components/Primitives";

export function SelectionDialog({
  document,
  diagramId,
  selection,
  repositories,
  repositoryId,
  dirty,
  canCreate,
  sourceRevision,
  sourceCommitId,
  onClose,
  onOperations,
  onExtract,
}: {
  document: ModelDocument;
  diagramId: string;
  selection: string[];
  repositories: Repository[];
  repositoryId: string;
  dirty: boolean;
  canCreate: boolean;
  sourceRevision: number;
  sourceCommitId: string;
  onClose: () => void;
  onOperations: (ops: Operation[], summary: string) => void | boolean;
  onExtract: (data: any) => Promise<void>;
}) {
  const source = document.diagrams.find((d) => d.diagramId === diagramId);
  const roots = document.objects.filter(
    (o) => !o.ownerObjectId && o.representationId === source?.representationId,
  );
  const [mode, setMode] = useState("view");
  const [name, setName] = useState("");
  const [ids, setIds] = useState<string[]>(
    roots
      .filter(
        (o) =>
          selection.includes(o.objectId) ||
          document.objects.some(
            (c) =>
              c.ownerObjectId === o.objectId && selection.includes(c.objectId),
          ),
      )
      .map((o) => o.objectId),
  );
  const [repo, setRepo] = useState(
    repositoryId || repositories[0]?.repositoryId || "",
  );
  const [preview, setPreview] = useState<any>();
  const [error, setError] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setPreview(undefined);
    setAck(false);
  }, [document]);
  const inspect = () => {
    setError("");
    setAck(false);
    try {
      if (mode === "view") {
        const operations = createSubjectArea(document, name, ids, diagramId);
        setPreview({
          operations,
          diagnostics: [],
          lossReport: {
            preserved: [
              "Selected semantic object identities",
              "Existing fields, keys and relationship meaning",
            ],
            translated: ["New independent diagram placement"],
            preservedOpaque: [],
            lost: [],
            unresolved: [],
          },
        });
      } else {
        const result = extractModel(document, name, ids, diagramId);
        result.document.extensions.extraction = {
          sourceModelId: document.model.modelId,
          sourceRevision,
          sourceCommitId,
          relationship: "independent-copy",
        };
        setPreview(result);
      }
    } catch (e) {
      setError(errorText(e));
    }
  };
  const apply = async () => {
    if (mode === "view") {
      const accepted = onOperations(
        preview.operations,
        `Create subject view ${name}`,
      );
      if (accepted !== false) onClose();
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onExtract({
        name,
        repositoryId: repo,
        modelType: preview.document.model.modelType,
        family: preview.document.model.family,
        targetProfile: preview.document.model.targetProfile,
        initialDocument: preview.document,
      });
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const hasGaps = !!preview?.lossReport?.unresolved?.length;
  return (
    <Modal
      title="Create from a selection"
      subtitle="Choose whether to share the same objects or create independent identities."
      wide
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          {preview ? (
            <Button
              variant="primary"
              busy={busy}
              disabled={
                preview.diagnostics?.some(
                  (d: Diagnostic) => d.severity === "error",
                ) ||
                (hasGaps && !ack) ||
                (mode === "copy" && (dirty || !canCreate))
              }
              onClick={() => void apply()}
            >
              {mode === "view"
                ? "Add subject view to draft"
                : "Create independent model"}
            </Button>
          ) : (
            <Button
              variant="primary"
              icon={Search}
              disabled={
                !ids.length ||
                !name.trim() ||
                (mode === "copy" && (dirty || !canCreate))
              }
              onClick={inspect}
            >
              Preview selection
            </Button>
          )}
        </>
      }
    >
      <div className="form-row">
        <Field label="Selection behavior">
          <select
            value={mode}
            onChange={(e) => {
              setMode(e.target.value);
              setPreview(undefined);
            }}
          >
            <option value="view">
              Subject view · same objects, separate layout
            </option>
            <option value="copy" disabled={!canCreate}>
              Independent model · new identities and source provenance
            </option>
          </select>
        </Field>
        <Field label={mode === "view" ? "View name" : "New model name"}>
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setPreview(undefined);
            }}
            placeholder={
              mode === "view"
                ? "e.g. Customer identity"
                : "e.g. Customer subsystem"
            }
          />
        </Field>
      </div>
      {mode === "copy" ? (
        <>
          <Notice>
            Source objects remain unchanged. The copy has its own identities and
            will not synchronize automatically.
          </Notice>
          {dirty && (
            <Notice kind="warning">
              Save or discard local model and inspector changes before creating
              an independent copy. Extraction must refer to a saved source
              revision.
            </Notice>
          )}
          <Field label="Destination repository">
            <select value={repo} onChange={(e) => setRepo(e.target.value)}>
              {repositories.map((r) => (
                <option key={r.repositoryId} value={r.repositoryId}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
        </>
      ) : (
        <Notice>
          A subject view references the same entities and fields. Semantic
          changes are shared across views; layout is independent.
        </Notice>
      )}
      <div className="section-label">Entities in this diagram</div>
      <div className="selection-object-list">
        {roots.map((o) => (
          <label className="check-row" key={o.objectId}>
            <input
              type="checkbox"
              checked={ids.includes(o.objectId)}
              onChange={(e) => {
                setIds(
                  e.target.checked
                    ? [...ids, o.objectId]
                    : ids.filter((id) => id !== o.objectId),
                );
                setPreview(undefined);
              }}
            />
            <span>{o.name}</span>
            <small>
              {
                document.objects.filter((c) => c.ownerObjectId === o.objectId)
                  .length
              }{" "}
              owned fields / keys
            </small>
          </label>
        ))}
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {preview && (
        <>
          <div className="preview-counts">
            <span>{ids.length} selected root objects</span>
            <span>
              {mode === "view"
                ? "Stable object identities retained"
                : `${preview.document.objects.length} new object identities`}
            </span>
            <span>
              {mode === "view"
                ? "Independent layout"
                : `${preview.document.relationships.length} internal relationships`}
            </span>
          </div>
          <Diagnostics items={preview.diagnostics || []} />
          <LossSummary loss={preview.lossReport} />
          {hasGaps && (
            <label className="check-row">
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
              />
              I reviewed the unresolved external relationships; they remain
              explicit dependencies in the copy.
            </label>
          )}
        </>
      )}
    </Modal>
  );
}
